import {
  entityRefToUrn,
  WitboostSystem,
} from '@agilelab/plugin-wb-builder-common';
import { expandEntityRef } from '@agilelab/plugin-wb-platform-common';
import {
  teamRolesManageLimitedPermission,
  teamRolesManagePermission,
  teamRolesTroubleshootPermission,
} from '@agilelab/plugin-wb-rbac-common';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { useCallback, useState } from 'react';
import { useAsync } from 'react-use';
import { teamRolesApiRef } from '../plugin';
import {
  EnabledTeamRoleInfo,
  ResolveTeamRoleParams,
  TeamRoleEnum,
} from '@agilelab/plugin-wb-team-roles-common';

type TeamRolesPanelProps = {
  project: WitboostSystem;
};

type TeamRolesPanelState = {
  isFeatureEnabled: boolean;
  canAccess: boolean;
  hasTroubleshootPermission: boolean;
  hasOwnerPermission: boolean;
  hasOwnerLimitedPermission: boolean;
  hasPrincipalOwner: boolean;
  isCatalogInfoOwner: boolean;
  enabledTeamRoles: EnabledTeamRoleInfo[];
  refresh: () => void;
  revokeAccess: () => void;
};

export const useTeamRolesPanel = ({
  project,
}: TeamRolesPanelProps): TeamRolesPanelState => {
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);
  const [accessRevoked, setAccessRevoked] = useState(false);
  const revokeAccess = useCallback(() => setAccessRevoked(true), []);
  const identityApi = useApi(identityApiRef);
  const teamRolesApi = useApi(teamRolesApiRef);

  const stringifiedEntityRef = stringifyEntityRef({
    kind: project?.kind ?? 'system',
    namespace: 'default',
    name: project?.metadata.name ?? '',
  });

  const projectUrn = entityRefToUrn(stringifiedEntityRef);

  const { value: teamRolesResolveResponse } = useAsync(async () => {
    const options = await identityApi.getCredentials();

    const params: ResolveTeamRoleParams = {
      teamRole: TeamRoleEnum.OWNER,
      projectUrn: projectUrn,
      mode: 'exact-match',
      useFallback: false,
      options: options,
    };

    return await teamRolesApi.resolve(params);
  }, [teamRolesApi, refreshKey]);

  const { value: teamRolesStatus } = useAsync(async () => {
    const options = await identityApi.getCredentials();
    const response = await teamRolesApi.getEnabledTeamRoles({
      urn: projectUrn,
      options,
    });

    return {
      enabledTeamRoles: response?.teamRoles ?? [],
      isFeatureEnabled: response?.enabled ?? false,
    };
  }, [teamRolesApi, refreshKey]);

  const { allowed: hasTroubleshootPermission } = usePermission({
    permission: teamRolesTroubleshootPermission,
    resourceRef: stringifiedEntityRef,
  });

  const { allowed: hasOwnerPermission } = usePermission({
    permission: teamRolesManagePermission,
    resourceRef: stringifiedEntityRef,
  });

  const { allowed: hasLimitedOwnerPermission } = usePermission({
    permission: teamRolesManageLimitedPermission,
    resourceRef: stringifiedEntityRef,
  });

  const { value: userInfo } = useAsync(async () => {
    if (!teamRolesStatus?.isFeatureEnabled) return undefined;
    return identityApi.getBackstageIdentity();
  }, [teamRolesStatus?.isFeatureEnabled, identityApi]);

  const { value: hasPrincipalOwner } = useAsync(async () => {
    if (
      !teamRolesStatus?.isFeatureEnabled ||
      !teamRolesResolveResponse?.subjects
    )
      return false;

    return teamRolesResolveResponse.subjects.some(
      subject => subject.exactMatch && !subject.isLimited,
    );
  }, [teamRolesStatus?.isFeatureEnabled, teamRolesResolveResponse?.subjects]);

  const { value: isCatalogInfoOwner } = useAsync(async () => {
    if (
      !userInfo?.userEntityRef ||
      !teamRolesStatus?.isFeatureEnabled ||
      !teamRolesResolveResponse?.subjects
    )
      return false;

    return (
      userInfo.userEntityRef ===
      expandEntityRef(
        project.spec.mesh.projectOwner ??
          project.spec.mesh.dataProductOwner ??
          '',
      )
    );
  }, [
    userInfo?.userEntityRef,
    project.spec.mesh.projectOwner,
    project.spec.mesh.dataProductOwner,
    teamRolesStatus?.isFeatureEnabled,
    teamRolesResolveResponse?.subjects,
  ]);

  return {
    isFeatureEnabled: teamRolesStatus?.isFeatureEnabled ?? false,
    canAccess:
      !accessRevoked &&
      (hasTroubleshootPermission ||
        hasOwnerPermission ||
        hasLimitedOwnerPermission ||
        ((isCatalogInfoOwner && !hasPrincipalOwner) ?? false)),
    hasTroubleshootPermission: hasTroubleshootPermission,
    hasOwnerPermission: hasOwnerPermission,
    hasOwnerLimitedPermission: hasLimitedOwnerPermission,
    hasPrincipalOwner: hasPrincipalOwner ?? false,
    isCatalogInfoOwner: isCatalogInfoOwner ?? false,
    enabledTeamRoles: teamRolesStatus?.enabledTeamRoles ?? [],
    refresh,
    revokeAccess,
  };
};
